import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { MapControls } from 'three/examples/jsm/controls/MapControls.js';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js';
import Sizes from './Utils/Sizes';
import Time from './Utils/Time';
import * as TWEEN from '@tweenjs/tween.js';
import { singleton } from 'tsyringe';
import Renderer from './Components/Renderer';
import { AudioListener, Frustum, Group, Matrix4, PerspectiveCamera, Vector3 } from 'three';
import * as ThreeMeshUI from 'three-mesh-ui';

@singleton()
export default class Camera {
    public instance: PerspectiveCamera;
    public orbitControls: OrbitControls;
    public cameraGroup: Group = new Group();

    public pointerLockControls: PointerLockControls;
    public audioListener: AudioListener = new AudioListener();

    private keyStates: { [key: string]: boolean } = {};

    private matrix: Matrix4 = new Matrix4();
    public frustum: Frustum = new Frustum();

    private eventHandlers: {
        resize: Array<{ event: string; callback: (...args: any[]) => void }>;
        tick: Array<{ event: string; callback: (...args: any[]) => void }>;
        keyboard: {
            keydown?: (e: KeyboardEvent) => void;
            keyup?: (e: KeyboardEvent) => void;
        };
    } = {
        resize: [],
        tick: [],
        keyboard: {},
    };

    public constructor(public time?: Time, public sizes?: Sizes, public renderer?: Renderer) {}


    public setInstance() {
        const { width, height } = this.sizes.viewport;
        this.instance = new PerspectiveCamera(75, width / height, 0.1, 10000);
        this.instance.position.set(7, 8, 10);
        this.instance.add(this.audioListener);

        const resizeHandler1 = () => {
            const { width, height } = this.sizes.viewport;
            this.instance.aspect = width / height;
            this.instance.updateProjectionMatrix();
        };
        this.sizes.on('resize', resizeHandler1);
        this.eventHandlers.resize.push({ event: 'resize', callback: resizeHandler1 });

        if (this.sizes && this.renderer) {
            const resizeHandler2 = () => {
                const { width, height } = this.sizes.viewport;
                if (this.renderer.webGLRenderer) {
                    this.renderer.webGLRenderer.setSize(width, height);
                    this.renderer.webGLRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
                }
            };
            this.sizes.on('resize', resizeHandler2);
            this.eventHandlers.resize.push({ event: 'resize', callback: resizeHandler2 });
        }

        this.cameraGroup.add(this.instance);
    }

    public resetAfterHMR() {

        this.dispose();

        const currentScene = this.renderer.scene;
        if (!currentScene.children.includes(this.cameraGroup)) {
            console.warn('⚠️ cameraGroup not in current scene, re-adding');
            currentScene.add(this.cameraGroup);
        }

        if (!this.cameraGroup.children.includes(this.instance)) {
            console.warn('⚠️ camera instance not in cameraGroup, re-adding');
            this.cameraGroup.add(this.instance);
        }

        this.setDefaultCameraPosition();

        const { width, height } = this.sizes.viewport;
        this.instance.aspect = width / height;
        this.instance.updateProjectionMatrix();

        this.setOrbitControls();
        this.setKeyboardControls();

        console.log('✅ Camera reset complete:', {
            position: this.instance.position.toArray(),
            target: this.orbitControls?.target.toArray(),
            groupPosition: this.cameraGroup.position.toArray(),
            inScene: currentScene.children.includes(this.cameraGroup),
            inGroup: this.cameraGroup.children.includes(this.instance),
        });
    }

    public dispose() {
        console.log('🧹 Disposing camera...', {
            hasOrbitControls: !!this.orbitControls,
            hasPointerLock: !!this.pointerLockControls,
            resizeListeners: this.eventHandlers.resize.length,
            tickListeners: this.eventHandlers.tick.length,
            keyboardListeners: Object.keys(this.eventHandlers.keyboard).length,
        });

        if (this.orbitControls) {
            this.orbitControls.dispose();
            this.orbitControls = null;
            console.log('  ✓ OrbitControls disposed');
        }

        if (this.pointerLockControls) {
            this.pointerLockControls.disconnect();
            this.pointerLockControls = null;
            console.log('  ✓ PointerLockControls disposed');
        }

        if (this.eventHandlers.keyboard.keydown) {
            window.removeEventListener('keydown', this.eventHandlers.keyboard.keydown);
            console.log('  ✓ keydown removed');
        }
        if (this.eventHandlers.keyboard.keyup) {
            window.removeEventListener('keyup', this.eventHandlers.keyboard.keyup);
            console.log('  ✓ keyup removed');
        }

        this.eventHandlers.resize.forEach(({ event, callback }) => {
            this.sizes.off(event, callback);
        });
        console.log(`  ✓ ${this.eventHandlers.resize.length} resize listeners removed`);

        this.eventHandlers.tick.forEach(({ event, callback }) => {
            this.time.off(event, callback);
        });
        console.log(`  ✓ ${this.eventHandlers.tick.length} tick listeners removed`);

        this.keyStates = {};
        this.eventHandlers = {
            resize: [],
            tick: [],
            keyboard: {},
        };

        console.log('✅ Camera disposal complete');
    }


    public setKeyboardControls() {
        this.eventHandlers.keyboard.keydown = (event) => {
            this.keyStates[event.code] = true;
        };
        this.eventHandlers.keyboard.keyup = (event) => {
            this.keyStates[event.code] = false;
        };

        window.addEventListener('keydown', this.eventHandlers.keyboard.keydown);
        window.addEventListener('keyup', this.eventHandlers.keyboard.keyup);
    }

    private updateCameraMovement() {
        const moveSpeed = 5 * (this.time.delta / 1000);

        const forward = new Vector3();
        this.instance.getWorldDirection(forward);
        forward.y = 0;
        forward.normalize();

        const right = new Vector3();
        right.crossVectors(this.instance.up, forward).normalize();

        const moveDirection = new Vector3();

        if (this.keyStates['ArrowUp'] || this.keyStates['KeyW']) {
            moveDirection.add(forward);
        }
        if (this.keyStates['ArrowDown'] || this.keyStates['KeyS']) {
            moveDirection.sub(forward);
        }
        if (this.keyStates['ArrowLeft'] || this.keyStates['KeyA']) {
            moveDirection.add(right);
        }
        if (this.keyStates['ArrowRight'] || this.keyStates['KeyD']) {
            const left = right.clone().negate();
            moveDirection.add(left);
        }

        if (moveDirection.length() > 0) {
            moveDirection.normalize().multiplyScalar(moveSpeed);

            this.instance.position.add(moveDirection);
            this.orbitControls.target.add(moveDirection);
        }
    }

    public setOrbitControls() {
        if (this.orbitControls) {
            this.orbitControls.dispose();
        }

        this.orbitControls = new MapControls(this.instance, this.renderer.webGLRenderer.domElement);
        this.orbitControls.enableDamping = true;
        this.orbitControls.enablePan = true;
        this.orbitControls.dampingFactor = 0.1;
        this.orbitControls.maxPolarAngle = Math.PI / 2;
        this.orbitControls.rotateSpeed = 0.3;
        this.orbitControls.maxDistance = 50;
        this.orbitControls.minDistance = 0;
        this.orbitControls.target = new Vector3(0, 0, 0);

        const updateMovementHandler = () => {
            this.updateCameraMovement();
            this.orbitControls.update();
        };
        this.time.on('tick', updateMovementHandler);
        this.eventHandlers.tick.push({ event: 'tick', callback: updateMovementHandler });

        const tweenHandler = () => TWEEN.update();
        this.time.on('tick', tweenHandler);
        this.eventHandlers.tick.push({ event: 'tick', callback: tweenHandler });

        const meshUIHandler = () => {
            try {
                ThreeMeshUI.update();
            } catch (e) {
                // console.warn(e);
            }
        };
        this.time.on('tick', meshUIHandler);
        this.eventHandlers.tick.push({ event: 'tick', callback: meshUIHandler });

        const frustumHandler = () => {
            this.matrix.multiplyMatrices(this.instance.projectionMatrix, this.instance.matrixWorldInverse);
            this.frustum.setFromProjectionMatrix(this.matrix);
        };
        this.time.on('tick', frustumHandler);
        this.eventHandlers.tick.push({ event: 'tick', callback: frustumHandler });

        const maxX = 40;
        const minX = -40;
        const maxZ = 40;
        const minZ = -40;

        this.orbitControls.addEventListener('change', () => {
            const x = this.orbitControls.target.x;
            const z = this.orbitControls.target.z;

            if (x < minX) this.orbitControls.target.x = minX + 0.1;
            if (x > maxX) this.orbitControls.target.x = maxX - 0.1;
            if (z < minZ) this.orbitControls.target.z = minZ + 0.1;
            if (z > maxZ) this.orbitControls.target.z = maxZ - 0.1;
        });
    }

    public disableOrbitControls() {
        if (this.orbitControls) {
            this.orbitControls.enabled = false;
            this.orbitControls.enableRotate = false;
            this.orbitControls.enablePan = false;
        }
    }

    public enableOrbitControls() {
        if (this.orbitControls) {
            this.orbitControls.enabled = true;
            this.orbitControls.enableRotate = true;
            this.orbitControls.enablePan = true;
        }
    }

    public setPointerLockControls() {
        if (this.orbitControls) {
            this.orbitControls.dispose();
            this.orbitControls = null;
        }

        this.pointerLockControls = new PointerLockControls(this.instance, this.renderer.webGLRenderer.domElement);

        // this.time.on('tick', () => this.pointerLockControls.update());
    }

    public setDefaultCameraPosition() {
        this.instance.position.set(10, 10, 10);
        this.cameraGroup.position.set(0, 0, 0);
        if (this.orbitControls) {
            this.orbitControls.target = new Vector3(0, 2, 0);
        }
    }

    public moveCameraTo(target: Vector3) {
        const newTarget = target.clone();

        new TWEEN.Tween({
            x: this.orbitControls.target.x,
            y: this.orbitControls.target.y,
            z: this.orbitControls.target.z,
        })
            .to(
                {
                    x: newTarget.x,
                    y: newTarget.y,
                    z: newTarget.z,
                },
                300,
            )
            .easing(TWEEN.Easing.Cubic.InOut)
            .start()
            .onUpdate((object) => {
                const oldTargetPosition = this.orbitControls.target.clone();

                this.orbitControls.target = new Vector3(object.x, -1.1, object.z);

                const dPosition = oldTargetPosition.sub(this.orbitControls.target);

                this.instance.position.sub(dPosition);
            });
    }

    public init() {
        this.setInstance();
        this.setOrbitControls();
        this.setKeyboardControls();
        // this.setPointerLockControls();
    }
}
